import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  X, Search, ArrowLeft, Package, Loader2, AlertCircle,
  LayoutGrid, List, Star, Plus,
} from 'lucide-react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import { groupProductsByVariant, type VariantEntry } from './variant-grouping';

export interface ProductGridItem {
  id: string;
  name: string;
  sku: string | null;
  category: string | null;
  hsnCode: string | null;
  unitPrice: number;
  costPrice: number;
  gstRate: number;
  /** 'inclusive' = unitPrice already contains GST; 'extra' = GST on top (Product.gstMode). */
  gstMode?: 'extra' | 'inclusive' | null;
  maxDiscount: number | null;
  discountApprovalEnabled?: boolean;
  productType?: string;
  unit: string | null;
  images: string[];
  /** Signed 400px thumb from GET /products. images[] holds `minio://` refs a browser cannot fetch. */
  imageUrl?: string | null;
  inventory?: { quantity: number }[];
  parentSku?: string | null;
  variantAxes?: Record<string, unknown> | null;
}

interface ProductGridSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (product: ProductGridItem) => void;
}

interface CategoryInfo {
  name: string;
  count: number;
}

type ViewMode = 'grid' | 'list';

const PINNED_STORAGE_KEY = 'aura_pinned_products';
const VIEW_MODE_STORAGE_KEY = 'aura_product_view';
const MAX_PINNED = 10;

// The /products endpoint paginates with a DEFAULT limit of 20. Every fetch in this
// picker must pass an explicit high limit or large categories get silently truncated
// (e.g. "HEAT PUMP" card promises 153 items, category view could only ever show 20).
const PRODUCTS_FETCH_LIMIT = '500';

function getPinnedIds(): string[] {
  try {
    const stored = localStorage.getItem(PINNED_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
}

function setPinnedIds(ids: string[]) {
  localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(ids));
}

function getViewMode(): ViewMode {
  try {
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return stored === 'list' ? 'list' : 'grid';
  } catch { return 'grid'; }
}

function setViewMode(mode: ViewMode) {
  localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
}

// Deterministic gradient from category name
function categoryGradient(name: string): string {
  const gradients = [
    'from-blue-500 to-blue-600',
    'from-emerald-500 to-emerald-600',
    'from-violet-500 to-violet-600',
    'from-amber-500 to-amber-600',
    'from-rose-500 to-rose-600',
    'from-cyan-500 to-cyan-600',
    'from-fuchsia-500 to-fuchsia-600',
    'from-orange-500 to-orange-600',
    'from-teal-500 to-teal-600',
    'from-indigo-500 to-indigo-600',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return gradients[Math.abs(hash) % gradients.length]!;
}

export function ProductGridSelector({ open, onClose, onSelect }: ProductGridSelectorProps) {
  const [view, setView] = useState<'categories' | 'products'>('categories');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [displayMode, setDisplayMode] = useState<ViewMode>(getViewMode);
  const [pinnedIds, setPinnedIdsState] = useState<string[]>(getPinnedIds);

  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [products, setProducts] = useState<ProductGridItem[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // All products cache for pinned section
  const [allProductsCache, setAllProductsCache] = useState<ProductGridItem[]>([]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setView('categories');
      setSelectedCategory(null);
      setSearchQuery('');
      setError(null);
      setPinnedIdsState(getPinnedIds());
    }
  }, [open]);

  // Fetch categories
  useEffect(() => {
    if (!open) return;
    setLoadingCategories(true);
    api.get('/products/categories')
      .then(res => {
        const data = res.data.data ?? res.data ?? [];
        if (data.length > 0 && typeof data[0] === 'string') {
          setCategories(data.map((name: string) => ({ name, count: 0 })));
        } else {
          setCategories(data);
        }
      })
      .catch(() => {
        api.get('/products', { params: { limit: PRODUCTS_FETCH_LIMIT } })
          .then(res => {
            const prods: ProductGridItem[] = res.data.data?.products ?? res.data.data ?? [];
            const catMap = new Map<string, number>();
            prods.forEach(p => {
              const cat = p.category || 'Uncategorized';
              catMap.set(cat, (catMap.get(cat) || 0) + 1);
            });
            setCategories(
              Array.from(catMap.entries()).map(([name, count]) => ({ name, count }))
            );
          })
          .catch(err => setError(err.response?.data?.error || 'Failed to load categories'));
      })
      .finally(() => setLoadingCategories(false));
  }, [open]);

  // Fetch all products for pinned section when modal opens
  useEffect(() => {
    if (!open || pinnedIds.length === 0) return;
    api.get('/products', { params: { limit: PRODUCTS_FETCH_LIMIT } })
      .then(res => {
        setAllProductsCache(res.data.data?.products ?? res.data.data ?? []);
      })
      .catch(() => { /* pinned section will just not show */ });
  }, [open, pinnedIds.length]);

  // Fetch products when a category is selected or "All Products" is chosen
  const fetchProducts = useCallback((category: string | null) => {
    setLoadingProducts(true);
    setError(null);
    const params: Record<string, string> = { limit: PRODUCTS_FETCH_LIMIT };
    if (category) params.category = category;
    api.get('/products', { params })
      .then(res => {
        const prods = res.data.data?.products ?? res.data.data ?? [];
        setProducts(prods);
        // Also update cache
        if (!category) setAllProductsCache(prods);
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load products'))
      .finally(() => setLoadingProducts(false));
  }, []);

  const handleCategoryClick = (categoryName: string) => {
    setSelectedCategory(categoryName);
    setView('products');
    setSearchQuery('');
    fetchProducts(categoryName);
  };

  const handleAllProducts = () => {
    setSelectedCategory(null);
    setView('products');
    setSearchQuery('');
    fetchProducts(null);
  };

  const handleBack = () => {
    setView('categories');
    setSelectedCategory(null);
    setSearchQuery('');
    setProducts([]);
  };

  const handleProductClick = (product: ProductGridItem) => {
    onSelect(product);
    onClose();
  };

  const togglePin = (productId: string) => {
    setPinnedIdsState(prev => {
      let next: string[];
      if (prev.includes(productId)) {
        next = prev.filter(id => id !== productId);
      } else {
        if (prev.length >= MAX_PINNED) return prev; // max reached
        next = [...prev, productId];
      }
      setPinnedIds(next);
      return next;
    });
  };

  const handleDisplayModeChange = (mode: ViewMode) => {
    setDisplayMode(mode);
    setViewMode(mode);
  };

  // Filter products by search
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return products;
    const q = searchQuery.toLowerCase();
    return products.filter(p =>
      p.name.toLowerCase().includes(q) ||
      (p.sku && p.sku.toLowerCase().includes(q))
    );
  }, [products, searchQuery]);

  // Pinned products resolved from cache
  const pinnedProducts = useMemo(() => {
    if (pinnedIds.length === 0) return [];
    const source = allProductsCache.length > 0 ? allProductsCache : products;
    return pinnedIds
      .map(id => source.find(p => p.id === id))
      .filter((p): p is ProductGridItem => !!p);
  }, [pinnedIds, allProductsCache, products]);

  // Global search across all products (when searching from category view)
  const [globalSearchResults, setGlobalSearchResults] = useState<ProductGridItem[]>([]);
  const [globalSearching, setGlobalSearching] = useState(false);

  // Esc / X close through the shared Modal (search text is not 'work', so no Discard card).

  useEffect(() => {
    if (view !== 'categories' || !searchQuery.trim()) {
      setGlobalSearchResults([]);
      return;
    }
    const timer = setTimeout(() => {
      setGlobalSearching(true);
      api.get('/products', { params: { search: searchQuery, limit: PRODUCTS_FETCH_LIMIT } })
        .then(res => {
          setGlobalSearchResults(res.data.data?.products ?? res.data.data ?? []);
        })
        .catch(() => setGlobalSearchResults([]))
        .finally(() => setGlobalSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [view, searchQuery]);

  if (!open) return null;

  return (
    <Modal
      open
      onClose={onClose}
      dirty={false}
      ariaLabel="Product picker"
      className="flex h-[calc(100vh-2rem)] !max-h-none !max-w-4xl flex-col !rounded-2xl !bg-white shadow-2xl sm:h-[calc(100vh-4rem)]"
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Header */}
        <div className="flex shrink-0 items-center gap-3 border-b border-gray-200 px-4 py-3 sm:px-6">
          {view === 'products' && (
            <button
              onClick={handleBack}
              aria-label="Back to categories"
              title="Back to categories"
              className="flex items-center gap-1 rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={view === 'categories' ? 'Search all products...' : `Search in ${selectedCategory || 'All Products'}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            />
          </div>
          {/* View Toggle */}
          <div className="flex items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 p-0.5">
            <button
              onClick={() => handleDisplayModeChange('grid')}
              className={`rounded-md p-1.5 transition-colors ${
                displayMode === 'grid'
                  ? 'bg-white text-[var(--ink-0)] shadow-sm'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
              title="Grid view"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => handleDisplayModeChange('list')}
              className={`rounded-md p-1.5 transition-colors ${
                displayMode === 'list'
                  ? 'bg-white text-[var(--ink-0)] shadow-sm'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
              title="List view"
            >
              <List size={16} />
            </button>
          </div>
          <CloseBtn />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {error && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Pinned / Frequently Used Section (always at top when pinned products exist) */}
          {pinnedProducts.length > 0 && (
            <div className="mb-5">
              <div className="mb-2 flex items-center gap-2">
                <Star size={14} className="fill-amber-400 text-amber-400" />
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Frequently Used</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {pinnedProducts.map(product => (
                  <button
                    key={`pinned-${product.id}`}
                    onClick={() => handleProductClick(product)}
                    className="group relative flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left transition-all hover:border-amber-300 hover:bg-amber-100"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-100">
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt="" className="h-full w-full rounded-md object-cover" />
                      ) : (
                        <Package size={14} className="text-amber-500" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-gray-800">{product.name}</p>
                      <p className="text-[10px] text-gray-500">{formatINR(product.unitPrice)}</p>
                    </div>
                    {/* span[role=button], NOT <button>: nesting a button inside the
                        outer card button is invalid HTML and browsers reparent it,
                        which can detach the unpin handler from the chip. */}
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label="Unpin product"
                      onClick={(e) => { e.stopPropagation(); togglePin(product.id); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          togglePin(product.id);
                        }
                      }}
                      className="ml-1 shrink-0 rounded p-0.5 text-amber-400 hover:bg-amber-200"
                      title="Unpin"
                    >
                      <Star size={12} className="fill-amber-400" />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Category View */}
          {view === 'categories' && !searchQuery.trim() && (
            <>
              {loadingCategories ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
                  <span className="ml-2 text-sm text-gray-500">Loading categories...</span>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {/* All Products card */}
                  <button
                    onClick={handleAllProducts}
                    className="group flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-5 text-center transition-all hover:border-[var(--ink-3)] hover:bg-[var(--saffron-50)]"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-200 transition-colors group-hover:bg-[var(--saffron-50)]">
                      <Package size={22} className="text-gray-500 group-hover:text-[var(--ink-0)]" />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 group-hover:text-[var(--ink-0)]">All Products</span>
                  </button>

                  {categories.map(cat => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryClick(cat.name)}
                      aria-label={`${cat.name}${cat.count > 0 ? ` — ${cat.count} ${cat.count === 1 ? 'item' : 'items'}` : ''}`}
                      className="group flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white p-5 text-center shadow-sm transition-all hover:shadow-md hover:border-[var(--ink-3)]"
                    >
                      <div className={`flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br ${categoryGradient(cat.name)}`}>
                        <Package size={22} className="text-white" />
                      </div>
                      <span className="text-sm font-semibold text-gray-800 line-clamp-2">{cat.name}</span>
                      {cat.count > 0 && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                          {cat.count} {cat.count === 1 ? 'item' : 'items'}
                        </span>
                      )}
                    </button>
                  ))}

                  {categories.length === 0 && !loadingCategories && (
                    <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-400">
                      <Package size={36} className="mb-2" />
                      <p className="text-sm">No categories found</p>
                      <button
                        onClick={handleAllProducts}
                        className="mt-2 text-sm text-[var(--ink-0)] hover:underline"
                      >
                        Browse all products
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Global search results (searching from category view) */}
          {view === 'categories' && searchQuery.trim() && (
            <>
              {globalSearching ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
                  <span className="ml-2 text-sm text-gray-500">Searching products...</span>
                </div>
              ) : globalSearchResults.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                  <Search size={36} className="mb-2" />
                  <p className="text-sm">No products match "{searchQuery}"</p>
                </div>
              ) : displayMode === 'list' ? (
                <ProductListView products={globalSearchResults} onSelect={handleProductClick} pinnedIds={pinnedIds} onTogglePin={togglePin} />
              ) : (
                <ProductGrid products={globalSearchResults} onSelect={handleProductClick} pinnedIds={pinnedIds} onTogglePin={togglePin} />
              )}
            </>
          )}

          {/* Product View */}
          {view === 'products' && (
            <>
              <div className="mb-4">
                <h3 className="text-base font-semibold text-gray-900">
                  {selectedCategory || 'All Products'}
                </h3>
                <p className="text-xs text-gray-500">
                  {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
                  {searchQuery.trim() && ` matching "${searchQuery}"`}
                </p>
              </div>
              {loadingProducts ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
                  <span className="ml-2 text-sm text-gray-500">Loading products...</span>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                  <Package size={36} className="mb-2" />
                  <p className="text-sm">No products found</p>
                </div>
              ) : displayMode === 'list' ? (
                <ProductListView products={filteredProducts} onSelect={handleProductClick} pinnedIds={pinnedIds} onTogglePin={togglePin} />
              ) : (
                <ProductGrid products={filteredProducts} onSelect={handleProductClick} pinnedIds={pinnedIds} onTogglePin={togglePin} />
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

function CloseBtn() {
  const { requestClose } = useModalClose();
  return (
    <button
      type="button"
      onClick={requestClose}
      aria-label="Close product picker"
      title="Close (Esc)"
      className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
    >
      <X size={20} />
    </button>
  );
}

// ==================== Grid View ====================

function ProductGrid({
  products,
  onSelect,
  pinnedIds,
  onTogglePin,
}: {
  products: ProductGridItem[];
  onSelect: (p: ProductGridItem) => void;
  pinnedIds: string[];
  onTogglePin: (id: string) => void;
}) {
  const entries = groupProductsByVariant(products);
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {entries.map((entry) =>
        entry.kind === 'single' ? (
          <SingleCard
            key={entry.product.id}
            product={entry.product}
            onSelect={onSelect}
            isPinned={pinnedIds.includes(entry.product.id)}
            onTogglePin={onTogglePin}
          />
        ) : (
          <VariantCard key={entry.parentSku} entry={entry} onSelect={onSelect} />
        ),
      )}
    </div>
  );
}

function SingleCard({
  product,
  onSelect,
  isPinned,
  onTogglePin,
}: {
  product: ProductGridItem;
  onSelect: (p: ProductGridItem) => void;
  isPinned: boolean;
  onTogglePin: (id: string) => void;
}) {
  const stockQty = product.inventory?.reduce((sum, inv) => sum + Number(inv.quantity || 0), 0) ?? null;
  const hasImage = !!product.imageUrl;
  return (
    <div className="group relative flex flex-col rounded-xl border border-gray-200 bg-white text-left shadow-sm transition-all hover:shadow-md hover:border-[var(--ink-3)]">
      <button
        onClick={(e) => { e.stopPropagation(); onTogglePin(product.id); }}
        className={`absolute right-2 top-2 z-10 rounded-full p-1 transition-all ${
          isPinned ? 'bg-amber-100 text-amber-500' : 'bg-white/80 text-gray-300 opacity-0 group-hover:opacity-100 hover:text-amber-400'
        }`}
        title={isPinned ? 'Unpin product' : 'Pin product'}
      >
        <Star size={14} className={isPinned ? 'fill-amber-400' : ''} />
      </button>
      <button onClick={() => onSelect(product)} className="flex flex-1 flex-col text-left">
        <div className="flex h-28 items-center justify-center rounded-t-xl bg-gray-100 sm:h-32">
          {hasImage ? (
            <img src={product.imageUrl ?? undefined} alt={product.name} className="h-full w-full rounded-t-xl object-cover" />
          ) : (
            <Package size={32} className="text-gray-300" />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-1 p-3">
          <span className="text-sm font-semibold text-gray-800 line-clamp-2 group-hover:text-[var(--ink-0)]">{product.name}</span>
          {product.sku && <span className="font-mono text-[11px] text-gray-400">{product.sku}</span>}
          <span className="text-sm font-bold text-gray-900">{formatINR(product.unitPrice)}</span>
          <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1">
            <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">GST {product.gstRate}%</span>
            {stockQty !== null && (
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${stockQty > 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {stockQty > 0 ? `${stockQty} in stock` : 'Out of stock'}
              </span>
            )}
          </div>
        </div>
      </button>
    </div>
  );
}

function VariantCard({
  entry,
  onSelect,
}: {
  entry: Extract<VariantEntry<ProductGridItem>, { kind: 'group' }>;
  onSelect: (p: ProductGridItem) => void;
}) {
  const [idx, setIdx] = useState(0);
  const chosen = entry.options[idx]?.product ?? entry.options[0]!.product;
  const hasImage = !!chosen.imageUrl;
  return (
    <div className="relative flex flex-col rounded-xl border border-gray-200 bg-white text-left shadow-sm transition-all hover:shadow-md hover:border-[var(--ink-3)]">
      <div className="flex h-28 items-center justify-center rounded-t-xl bg-gray-100 sm:h-32">
        {hasImage ? (
          <img src={chosen.imageUrl ?? undefined} alt={entry.baseName} className="h-full w-full rounded-t-xl object-cover" />
        ) : (
          <Package size={32} className="text-gray-300" />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <span className="text-sm font-semibold text-gray-800 line-clamp-2">{entry.baseName}</span>
        <label className="mt-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">{entry.axisLabel}</label>
        <select
          value={idx}
          onChange={(e) => setIdx(Number(e.target.value))}
          className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs focus:border-[var(--ink-0)] focus:outline-none"
        >
          {entry.options.map((o, i) => (
            <option key={o.value} value={i}>{o.label}</option>
          ))}
        </select>
        <span className="mt-1 text-sm font-bold text-gray-900">{formatINR(chosen.unitPrice)}</span>
        <button
          onClick={() => onSelect(chosen)}
          className="mt-2 flex items-center justify-center gap-1 rounded-md bg-[var(--saffron-50)] py-1.5 text-xs font-semibold text-[var(--ink-0)] transition-colors hover:bg-[var(--saffron-100)]"
        >
          <Plus size={13} /> Add
        </button>
      </div>
    </div>
  );
}

// ==================== List View ====================

function ProductListView({
  products,
  onSelect,
  pinnedIds,
  onTogglePin,
}: {
  products: ProductGridItem[];
  onSelect: (p: ProductGridItem) => void;
  pinnedIds: string[];
  onTogglePin: (id: string) => void;
}) {
  const entries = groupProductsByVariant(products);
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="px-3 py-2 text-xs font-semibold text-gray-500">Name</th>
            <th className="px-3 py-2 text-xs font-semibold text-gray-500">SKU / Variant</th>
            <th className="px-3 py-2 text-xs font-semibold text-gray-500 text-right">Price</th>
            <th className="px-3 py-2 text-xs font-semibold text-gray-500 text-right">GST%</th>
            <th className="px-3 py-2 text-xs font-semibold text-gray-500">Category</th>
            <th className="px-3 py-2 text-xs font-semibold text-gray-500 w-16"></th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) =>
            entry.kind === 'single' ? (
              <SingleRow
                key={entry.product.id}
                product={entry.product}
                onSelect={onSelect}
                isPinned={pinnedIds.includes(entry.product.id)}
                onTogglePin={onTogglePin}
              />
            ) : (
              <VariantRow key={entry.parentSku} entry={entry} onSelect={onSelect} />
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}

function SingleRow({
  product,
  onSelect,
  isPinned,
  onTogglePin,
}: {
  product: ProductGridItem;
  onSelect: (p: ProductGridItem) => void;
  isPinned: boolean;
  onTogglePin: (id: string) => void;
}) {
  return (
    <tr className="border-b border-gray-100 transition-colors hover:bg-[var(--saffron-50)]">
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onTogglePin(product.id); }}
            className={`shrink-0 rounded p-0.5 transition-colors ${isPinned ? 'text-amber-400' : 'text-gray-300 hover:text-amber-400'}`}
            title={isPinned ? 'Unpin' : 'Pin'}
          >
            <Star size={12} className={isPinned ? 'fill-amber-400' : ''} />
          </button>
          <span className="font-medium text-gray-800 truncate max-w-[200px]">{product.name}</span>
        </div>
      </td>
      <td className="px-3 py-1.5 font-mono text-xs text-gray-400">{product.sku || '-'}</td>
      <td className="px-3 py-1.5 text-right font-semibold text-gray-900">{formatINR(product.unitPrice)}</td>
      <td className="px-3 py-1.5 text-right text-gray-600">{product.gstRate}%</td>
      <td className="px-3 py-1.5 text-xs text-gray-500 truncate max-w-[120px]">{product.category || '-'}</td>
      <td className="px-3 py-1.5">
        <button
          onClick={() => onSelect(product)}
          className="flex items-center justify-center rounded-md bg-[var(--saffron-50)] p-1 text-[var(--ink-0)] transition-colors hover:bg-[var(--saffron-50)]"
          title="Add product"
        >
          <Plus size={14} />
        </button>
      </td>
    </tr>
  );
}

function VariantRow({
  entry,
  onSelect,
}: {
  entry: Extract<VariantEntry<ProductGridItem>, { kind: 'group' }>;
  onSelect: (p: ProductGridItem) => void;
}) {
  const [idx, setIdx] = useState(0);
  const chosen = entry.options[idx]?.product ?? entry.options[0]!.product;
  return (
    <tr className="border-b border-gray-100 transition-colors hover:bg-[var(--saffron-50)]">
      <td className="px-3 py-1.5">
        <span className="font-medium text-gray-800 truncate max-w-[200px]">{entry.baseName}</span>
      </td>
      <td className="px-3 py-1.5">
        <select
          value={idx}
          onChange={(e) => setIdx(Number(e.target.value))}
          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs focus:border-[var(--ink-0)] focus:outline-none"
          title={entry.axisLabel}
        >
          {entry.options.map((o, i) => (
            <option key={o.value} value={i}>{o.label}</option>
          ))}
        </select>
      </td>
      <td className="px-3 py-1.5 text-right font-semibold text-gray-900">{formatINR(chosen.unitPrice)}</td>
      <td className="px-3 py-1.5 text-right text-gray-600">{chosen.gstRate}%</td>
      <td className="px-3 py-1.5 text-xs text-gray-500 truncate max-w-[120px]">{chosen.category || '-'}</td>
      <td className="px-3 py-1.5">
        <button
          onClick={() => onSelect(chosen)}
          className="flex items-center justify-center rounded-md bg-[var(--saffron-50)] p-1 text-[var(--ink-0)] transition-colors hover:bg-[var(--saffron-50)]"
          title="Add variant"
        >
          <Plus size={14} />
        </button>
      </td>
    </tr>
  );
}
